/**
 * Config rules — PURE. Theme derivation and defaults.
 *
 * The whole point of deriving a palette from one merchant colour is that a merchant
 * can pick a light brand colour (yellow, mint) and the kiosk must still be readable.
 * Hardcoding white text on `primary` breaks exactly those merchants, so `onPrimary`
 * is computed from contrast rather than assumed.
 */
import { KIOSK_ROUTES } from "../navigation/routes";
import type { IdlePolicy } from "../session/types";
import type { KioskConfig, KioskTheme } from "./types";
import type { KioskRoute } from "../navigation/routes";
import { contrastRatio, mix, parseHex, tint, toHex } from "../../utils/color";

// the colour maths lives in `utils/color` (one home, DESIGN_SYSTEM.md §3.1); re-exported for this module's importers
export { contrastRatio, parseHex, relativeLuminance, tint } from "../../utils/color";

/**
 * Used until the config API responds, and if it returns an unusable colour.
 *
 * Every hex in this file is LOWERCASE, because `buildKioskTheme` normalises its output
 * to lowercase. One canonical form means a component can compare theme colours by
 * string without a casing surprise.
 */
export const FALLBACK_PRIMARY = "#5b2586";

/**
 * The non-merchant half of the palette, taken from the SOK-KIOSK Figma file
 * (node 854:21372, the catalog screen — the fullest screen in the design, so it carries
 * every neutral the others use).
 *
 * ⚠️ **`background` is the GREY and `surface` is the WHITE.** They used to be the other way
 * round (`#ffffff` page, `#f9fafb` cards), which is inverted from the design: the kiosk's page
 * is `#f8f8f8` and every card, the header and the footer sit on it in white. Screens read
 * `background` for the page and `surface` for anything raised, so getting this pair backwards
 * made cards invisible against their own page.
 */
const NEUTRALS = {
  /** The page. Cards, header and footer are `surface` ON this. */
  background: "#f8f8f8",
  /** Anything raised off the page: cards, chrome, modals, chips. */
  surface: "#ffffff",
  /** Hairlines: card edges, the category rail's divider, an outlined button. */
  border: "#e1e5ea",
  /**
   * The heavier hairline — chips, the search field, section dividers. The design draws it as
   * `rgba(17,28,54,0.2)`; this is that colour pre-blended over the page, for the reason the
   * primary tint ladder is pre-blended: a translucent border is only the colour it was drawn
   * as where the backdrop is exactly what the designer had behind it.
   */
  borderStrong: "#d0d3da",
  /** Content ink: an item's name, a price. */
  text: "#101010",
  /** Chrome ink: the header title, a category label. Deliberately a different colour. */
  textHeading: "#111c36",
  /** Secondary copy: a section's description, a struck-through price. */
  textMuted: "#9c939d",
  /** Behind an item photo while it loads, and where an item has none. */
  imagePlaceholder: "#f9f4ec",
  /** The discount badge. */
  success: "#51bb60",
  /**
   * A "Required" badge on a customisation step (461:9918) — a warm sand with its own ink.
   * Not `danger`: a step the customer has not answered yet is an instruction, not an error,
   * and the red badge this replaced read as one.
   */
  attention: "#fbf2e1",
  onAttention: "#8b5a20",
  danger: "#b91c1c",
} as const;

/**
 * The light side of the `onPrimary` decision — pure white, NOT `background`.
 *
 * It read `background` until the page colour became grey (above). Text on the merchant's
 * button has nothing to do with the page it sits on, and picking the page's grey for it was
 * only ever right by coincidence.
 */
const ON_PRIMARY_LIGHT = "#ffffff";

/** The tint ladder, as the design specifies it. See `KioskTheme` for each one's role. */
export const TINT_OPACITY = { subtle: 0.06, border: 0.36, disabled: 0.6 } as const;

/**
 * Derives the full palette from the merchant's primary colour.
 *
 * An unparseable or missing colour falls back rather than throwing — a bad config
 * value must not blank the kiosk.
 */
export function buildKioskTheme(primaryColor?: string): KioskTheme {
  const parsed = parseHex(primaryColor ?? "") ?? parseHex(FALLBACK_PRIMARY)!;
  const primary = `#${toHex(parsed.r)}${toHex(parsed.g)}${toHex(parsed.b)}`;

  // Pick whichever of white/near-black reads better ON the merchant's colour, so a
  // pale brand colour still gets legible button text.
  const onPrimary =
    contrastRatio(primary, ON_PRIMARY_LIGHT) >= contrastRatio(primary, NEUTRALS.text)
      ? ON_PRIMARY_LIGHT
      : NEUTRALS.text;

  return {
    primary,
    onPrimary,
    primaryPressed: mix(parsed, 0, 0.18),
    primarySubtle: tint(parsed, TINT_OPACITY.subtle),
    primaryBorder: tint(parsed, TINT_OPACITY.border),
    primaryDisabled: tint(parsed, TINT_OPACITY.disabled),
    ...NEUTRALS,
  };
}

/**
 * Maps the config's single ordering timeout onto the per-route policy.
 *
 * The API sends one value because that is the one a merchant reasons about. Payment
 * keeps a longer window (card entry is slow) and success keeps its short one, both
 * derived — resetting a customer mid-authorisation is the worst version of this bug.
 */
export function toIdleOverrides(
  config?: Pick<KioskConfig, "idleTimeoutSeconds">,
): Partial<Record<KioskRoute, Partial<IdlePolicy>>> | undefined {
  const seconds = config?.idleTimeoutSeconds;
  if (!seconds || seconds <= 0) return undefined;

  const ms = seconds * 1000;
  return {
    [KIOSK_ROUTES.SERVICE]: { timeoutMs: ms },
    [KIOSK_ROUTES.CATALOG]: { timeoutMs: ms },
    [KIOSK_ROUTES.SEARCH]: { timeoutMs: ms },
    [KIOSK_ROUTES.CART]: { timeoutMs: ms },
    [KIOSK_ROUTES.PAYMENT]: { timeoutMs: ms * 2 },
  };
}

export function sortByDisplayOrder<T extends { displayOrder?: number }>(items: T[]): T[] {
  return items.slice().sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}
